import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import '../../styles/TrainingView.css';
import mixpanel from '../../utils/mixpanel'
import { API_URL } from '../../app/constants'
import StreakUpgradeButton from '../../components/StreakUpgradeButton'
import PricingModal from '../../components/modals/PricingModal'

const ListeningChallenges = () => {
  const navigate = useNavigate();
  
  // States for listening sets
  const [listeningSets, setListeningSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [setToDelete, setSetToDelete] = useState(null);
  
  // Create set form states
  const [setName, setSetName] = useState('');
  const [youtubeLinks, setYoutubeLinks] = useState(['']);
  const [isCreating, setIsCreating] = useState(false);
  
  // User state
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    fetchUserData();
    fetchListeningSets();
  }, []);

  const fetchUserData = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUserStatus(userData.status);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchListeningSets = async () => {
    try {
      setLoading(true);
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/listening-sets`, {
        headers: { 'Authorization': `Bearer ${jwt}` },
      });
      
      if (response.ok) {
        const sets = await response.json();
        setListeningSets(sets);
      } else {
        setError('Failed to load listening sets');
      }
    } catch (error) {
      console.error('Error fetching listening sets:', error);
      setError('Error loading listening sets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSet = async () => {
    if (!setName.trim()) {
      alert('Please enter a set name');
      return;
    }

    const validLinks = youtubeLinks.filter(link => link.trim() !== '');
    if (validLinks.length === 0) {
      alert('Please enter at least one YouTube link');
      return;
    }

    try {
      setIsCreating(true);
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/listening-sets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          set_name: setName,
          youtube_links: validLinks
        })
      });

      if (response.ok) {
        // Reset form
        setSetName('');
        setYoutubeLinks(['']);
        setShowCreateModal(false);
        
        // Refresh the list
        fetchListeningSets();
        
        mixpanel.track('Listening Set Created', {
          set_name: setName,
          video_count: validLinks.length,
          is_premium: userStatus === 'active'
        });
      } else {
        const errorData = await response.json();
        if (response.status === 400 && errorData.error.includes('Premium') && userStatus !== 'active') {
          setShowPricingModal(true);
        } else {
          alert(errorData.error || 'Failed to create listening set');
        }
      }
    } catch (error) {
      console.error('Error creating listening set:', error);
      alert('Error creating listening set');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (set) => {
    setSetToDelete(set);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!setToDelete) return;

    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/listening-sets/${setToDelete.set_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${jwt}` },
      });

      if (response.ok) {
        fetchListeningSets(); // Refresh the list
        setShowDeleteModal(false);
        setSetToDelete(null);
      } else {
        alert('Failed to delete listening set');
      }
    } catch (error) {
      console.error('Error deleting listening set:', error);
      alert('Error deleting listening set');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSetToDelete(null);
  };

  const addYoutubeLink = () => {
    const maxLinks = userStatus === 'active' ? 10 : 5;
    if (youtubeLinks.length < maxLinks) {
      setYoutubeLinks([...youtubeLinks, '']);
    }
  };

  const removeYoutubeLink = (index) => {
    if (youtubeLinks.length > 1) {
      setYoutubeLinks(youtubeLinks.filter((_, i) => i !== index));
    }
  };

  const updateYoutubeLink = (index, value) => {
    const newLinks = [...youtubeLinks];
    newLinks[index] = value;
    setYoutubeLinks(newLinks);
  };

  return (
    <div className="training-view">
      <StreakUpgradeButton source="ListeningChallenges" />
      
      {/* Header */}
      <div className="dashboard-header">
        <h1>My Listening Sets</h1>
      </div>
      
      {/* Action Selector */}
      <div className="session-selector">
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            backgroundColor: 'white',
            color: '#9333EA',
            fontFamily: 'Manrope, sans-serif',
            fontSize: '14px',
            fontWeight: '600',
            padding: '12px 20px',
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: '160px',
            justifyContent: 'center'
          }}
        >
          <span style={{ fontSize: '16px', fontWeight: '400' }}>+</span>
          Create New Set
        </button>
      </div>

      {error && (
        <div className="error-state">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner-ripple">
            <div></div>
            <div></div>
          </div>
        </div>
      ) : (
        <div className="exercises-grid">
          {listeningSets.map((set) => (
            <div
              key={set.set_id}
              className="exercise-card"
              style={{ position: 'relative' }}
              onClick={() => {
                // Navigate to listening challenge for user sets - use a different route
                navigate(`/contests/user-set/${set.set_id}/0`);
              }}
            >
              <div className="exercise-card-content">
                <div className="exercise-info">
                  <span className="exercise-type">Listening Set</span>
                  <h3 className="exercise-title">{set.set_name}</h3>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#6B7280', 
                    margin: '4px 0 0 0',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    {set.video_count} video{set.video_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(set);
                  }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.6,
                    borderRadius: '4px',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '1';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '0.6';
                  }}
                >
                  <X size={16} color="#6B7280" />
                </button>
              </div>
            </div>
          ))}
          
          {listeningSets.length === 0 && !loading && (
            <div className="empty-state" style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: '16px',
              color: '#6B7280',
              textAlign: 'center',
              padding: '80px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              gridColumn: '1 / -1'
            }}>
              No listening sets yet. Create your first one!
            </div>
          )}
        </div>
      )}

      {/* Create Set Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          backdropFilter: 'blur(2px)'
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px 28px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{
              color: "#111827", 
              marginBottom: "24px",
              fontSize: '22px',
              fontWeight: '700',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Create New Listening Set
            </h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#374151',
                fontFamily: 'Manrope, sans-serif'
              }}>
                Set Name
              </label>
              <input
                type="text"
                value={setName}
                onChange={(e) => setSetName(e.target.value)}
                placeholder="Enter set name"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  fontSize: '15px',
                  fontFamily: 'Manrope, sans-serif',
                  minHeight: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                marginBottom: '8px',
                color: '#374151',
                fontFamily: 'Manrope, sans-serif'
              }}>
                YouTube Links ({userStatus === 'active' ? '10' : '5'} max)
              </label>
              {youtubeLinks.map((link, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => updateYoutubeLink(index, e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    style={{
                      flex: 1,
                      padding: '12px 14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      fontSize: '15px',
                      fontFamily: 'Manrope, sans-serif',
                      minHeight: '16px'
                    }}
                  />
                  {youtubeLinks.length > 1 && (
                    <button
                      onClick={() => removeYoutubeLink(index)}
                      style={{
                        padding: '12px',
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        minWidth: '44px'
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              
              {youtubeLinks.length < (userStatus === 'active' ? 10 : 5) && (
                <button
                  onClick={addYoutubeLink}
                  style={{
                    padding: '8px 16px',
                    background: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Manrope, sans-serif',
                    marginTop: '8px'
                  }}
                >
                  + Add Another Link
                </button>
              )}
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              flexDirection: 'column'
            }}>
              <button
                onClick={handleCreateSet}
                disabled={isCreating}
                style={{
                  backgroundColor: isCreating ? '#9CA3AF' : '#9334E9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: isCreating ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {isCreating ? 'Creating...' : 'Create Set'}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          backdropFilter: 'blur(2px)'
        }} onClick={handleDeleteCancel}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '32px 28px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{
              color: "#111827", 
              marginBottom: "16px",
              fontSize: '20px',
              fontWeight: '700',
              fontFamily: 'Manrope, sans-serif'
            }}>
              Delete Listening Set
            </h2>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '16px',
              lineHeight: '1.5',
              color: '#6B7280',
              fontFamily: 'Manrope, sans-serif',
              textAlign: 'center'
            }}>
              Are you sure you want to delete "<strong>{setToDelete?.set_name}</strong>"? This action cannot be undone.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: 'column'
            }}>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#EF4444';
                }}
              >
                Delete Set
              </button>
              <button
                onClick={handleDeleteCancel}
                style={{
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Manrope, sans-serif',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        source="ListeningChallenges - Create Set"
      />
    </div>
  );
};

export default ListeningChallenges;